import React, { Component, ReactNode } from 'react'
import * as Sentry from '@sentry/react'
import { IoRefresh, IoWarning } from 'react-icons/io5'

interface Props {
  children: ReactNode
  fallback?: ReactNode
  /** Name of the component/section for error tracking */
  name?: string
  /** Whether to show a compact error UI */
  compact?: boolean
  /** Custom onRetry callback */
  onRetry?: () => void
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: React.ErrorInfo | null
}

/**
 * Error Boundary component that catches JavaScript errors in child components.
 * Integrates with Sentry for error reporting and provides user-friendly error UI.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ errorInfo })

    // Log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo)

    // Report to Sentry if available
    if (typeof Sentry !== 'undefined' && Sentry.captureException) {
      Sentry.captureException(error, {
        extra: {
          componentStack: errorInfo.componentStack,
          boundaryName: this.props.name || 'unknown',
        },
        tags: {
          errorBoundary: this.props.name || 'unknown',
        },
      })
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null })
    this.props.onRetry?.()
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Compact error UI for smaller sections
      if (this.props.compact) {
        return (
          <div className="error-boundary-compact">
            <IoWarning size={16} />
            <span>Failed to load {this.props.name || 'content'}</span>
            <button onClick={this.handleRetry} className="error-retry-btn-small">
              <IoRefresh size={14} />
            </button>
          </div>
        )
      }

      // Full error UI
      return (
        <div className="error-boundary-card">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <IoWarning size={32} />
            </div>
            <h2 className="error-boundary-title">Something went wrong</h2>
            <p className="error-boundary-message">
              {this.getUserFriendlyMessage(this.state.error)}
            </p>
            {this.props.name && (
              <p className="error-boundary-context">
                Error in: {this.props.name}
              </p>
            )}
            <button className="error-retry-btn" onClick={this.handleRetry}>
              <IoRefresh size={18} />
              Try again
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }

  private getUserFriendlyMessage(error: Error | null): string {
    if (!error) return 'An unexpected error occurred. Please try again.'

    const message = error.message.toLowerCase()

    // Network errors
    if (message.includes('fetch') || message.includes('network')) {
      return 'Unable to connect. Please check your internet connection and try again.'
    }

    // API errors
    if (message.includes('api error')) {
      return 'The server encountered an issue. Please try again in a moment.'
    }

    // Timeout
    if (message.includes('timeout')) {
      return 'The request took too long. Please try again.'
    }

    // Generic fallback
    return 'An unexpected error occurred. Please try again.'
  }
}

/**
 * Higher-order component wrapper for functional components
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  name?: string
) {
  return function WithErrorBoundary(props: P) {
    return (
      <ErrorBoundary name={name}>
        <WrappedComponent {...props} />
      </ErrorBoundary>
    )
  }
}
